import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";
import { DateTime } from "luxon";
import { ReactElement } from "react";
import * as Yup from "yup";

import {
  CreatePriceOverrideMutation,
  CreatePriceOverrideMutationVariables,
  GetTenantPriceDataQuery,
} from "~frontend/__generated__/graphql";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikDatePicker from "~frontend/components/forms/FormikDatePicker";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { CREATE_PRICE_OVERRIDE } from "~frontend/graphql/priceOverrides";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import BackdatedPricingBudgetWarning from "./BackdatedPricingBudgetWarning";
import { dollarFractionAdornment } from "./dollarFractionAdornment";
import {
  dollarFractionValidator,
  parseDollarFractionAsMillicents,
} from "./priceFormatting";
import { useConfirmPrices } from "./useConfirmPrices";

type FormValues = {
  date: DateTime;
  mmsPriceDollarFraction: string;
  smsPriceDollarFraction: string;
  mmsUnregisteredPriceDollarFraction: string;
  smsUnregisteredPriceDollarFraction: string;
  mmsShortcodePriceDollarFraction: string;
  smsShortcodePriceDollarFraction: string;
  [submitErrorField]?: string;
};

export function CreatePriceOverrideDialog({
  open,
  onClose,
  defaults,
}: {
  open: boolean;
  onClose: () => void;
  defaults: GetTenantPriceDataQuery["getTenantPriceData"]["defaults"];
}): ReactElement {
  const [createPriceOverride] = useMutation<
    CreatePriceOverrideMutation,
    CreatePriceOverrideMutationVariables
  >(CREATE_PRICE_OVERRIDE);

  const confirmPrices = useConfirmPrices();

  const formatErrorMessage = useErrorMessageFormatter();

  const handleSubmit = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    const newPrices = {
      mmsMessagePriceInMillicents: parseDollarFractionAsMillicents(
        values.mmsPriceDollarFraction
      ),
      smsSegmentPriceInMillicents: parseDollarFractionAsMillicents(
        values.smsPriceDollarFraction
      ),
      mmsUnregisteredMessagePriceInMillicents: parseDollarFractionAsMillicents(
        values.mmsUnregisteredPriceDollarFraction
      ),
      smsUnregisteredSegmentPriceInMillicents: parseDollarFractionAsMillicents(
        values.smsUnregisteredPriceDollarFraction
      ),
      mmsShortcodeMessagePriceInMillicents: parseDollarFractionAsMillicents(
        values.mmsShortcodePriceDollarFraction
      ),
      smsShortcodeSegmentPriceInMillicents: parseDollarFractionAsMillicents(
        values.smsShortcodePriceDollarFraction
      ),
    };

    try {
      await confirmPrices({ newPrices, defaults });
    } catch (_e) {
      // user canceled
      return;
    }

    const { errors } = await createPriceOverride({
      variables: {
        input: {
          startDate: values.date.toISODate()!,
          ...newPrices,
        },
      },
    });

    if (errors) {
      formik.setErrors({
        [submitErrorField]: formatErrorMessage(errors),
      });
    } else {
      onClose();
      formik.resetForm();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Price Change</DialogTitle>
      <Formik
        initialValues={{
          date: DateTime.now(),
          mmsPriceDollarFraction: "",
          smsPriceDollarFraction: "",
          mmsUnregisteredPriceDollarFraction: "",
          smsUnregisteredPriceDollarFraction: "",
          mmsShortcodePriceDollarFraction: "",
          smsShortcodePriceDollarFraction: "",
        }}
        validationSchema={Yup.object({
          mmsPriceDollarFraction: dollarFractionValidator,
          smsPriceDollarFraction: dollarFractionValidator,
          mmsUnregisteredPriceDollarFraction: dollarFractionValidator,
          smsUnregisteredPriceDollarFraction: dollarFractionValidator,
          mmsShortcodePriceDollarFraction: dollarFractionValidator,
          smsShortcodePriceDollarFraction: dollarFractionValidator,
        })}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, isValid, values }) => (
          <Form>
            <DialogContent>
              <FormikErrorBanner noScrollOnError />
              <FormikDatePicker name="date" label="Effective Date" fullWidth />
              <FormikTextField
                name="smsPriceDollarFraction"
                label="SMS Price Per Segment"
                placeholder={defaults.smsSegmentPriceInMillicents
                  .toString()
                  .padStart(5, "0")}
                InputProps={dollarFractionAdornment}
                fullWidth
              />
              <FormikTextField
                name="mmsPriceDollarFraction"
                label="MMS Price Per Message"
                placeholder={defaults.mmsMessagePriceInMillicents
                  .toString()
                  .padStart(5, "0")}
                InputProps={dollarFractionAdornment}
                fullWidth
              />
              <FormikTextField
                name="smsUnregisteredPriceDollarFraction"
                label="Unregistered SMS Price Per Segment"
                placeholder={defaults.smsUnregisteredSegmentPriceInMillicents
                  .toString()
                  .padStart(5, "0")}
                InputProps={dollarFractionAdornment}
                fullWidth
              />
              <FormikTextField
                name="mmsUnregisteredPriceDollarFraction"
                label="Unregistered MMS Price Per Message"
                placeholder={defaults.mmsUnregisteredMessagePriceInMillicents
                  .toString()
                  .padStart(5, "0")}
                InputProps={dollarFractionAdornment}
                fullWidth
              />
              <FormikTextField
                name="smsShortcodePriceDollarFraction"
                label="Shortcode SMS Price Per Segment"
                placeholder={defaults.smsShortcodeSegmentPriceInMillicents
                  .toString()
                  .padStart(5, "0")}
                InputProps={dollarFractionAdornment}
                fullWidth
              />
              <FormikTextField
                name="mmsShortcodePriceDollarFraction"
                label="Shortcode MMS Price Per Message"
                placeholder={defaults.mmsShortcodeMessagePriceInMillicents
                  .toString()
                  .padStart(5, "0")}
                InputProps={dollarFractionAdornment}
                fullWidth
              />
              <BackdatedPricingBudgetWarning effectiveDate={values.date} />
            </DialogContent>
            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={onClose}
                color="secondary"
              >
                Cancel
              </Button>

              <FormikSubmitButton
                disabled={isSubmitting || !isValid}
                color="primary"
                variant="contained"
                submitText="Add"
                loadingText="Adding..."
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
